// ============================================================
// normalizeDesign — maps a popup row's design document into the full model
// ============================================================
//
// Every popup_settings row carries ONE style key, `style_settings.design`
// (the 2026 document). Rows created before the refactor carried pre-2026
// `welcome` / `success` colour blocks instead; on 8 Oct 2026 every one of
// them was rewritten into an equivalent design document (flowIQ
// scripts/popup-legacy-migrate.mjs, proven render-identical) and the server
// now refuses those keys. So this file has one job: base defaults → the
// stored document → per-step overrides.
//
// DEFAULTS (do not "improve" these without re-running the live-popup
// snapshot suite — they encode what the old renderer painted, and a design
// document only stores what differs from them):
// - panel background white, font Inter, input focus ring teal-500 (#14b8a6),
//   accent icon green-600 (#16a34a), centered modal, no teaser, 200ms fade.

import {
  PopupDesign,
  PopupDesignInput,
  NormalizedPopupDesign,
  ResolvedStepDesign,
  StepDesignOverrides,
  DesignTheme,
  DesignElements,
  DesignImage,
  DesignTextImage,
  DeepPartial,
} from "@/shared/types/popup-design";
// No import from popup-types on purpose: this file + popup-design.ts are
// mirrored VERBATIM into flowIQ's editor (scripts/popup-model-port.mjs there),
// so everything they need must live in these two files.

// ---------- legacy constants (what the old renderer hardcoded) ----------

const LEGACY = {
  fontFamily: "Inter",
  panelBackground: "#ffffff",
  overlayColor: "rgba(0,0,0,0.5)",
  headingColor: "#000000",
  subheadingColor: "#666666",
  bodyColor: "#333333",
  footerColor: "#6b7280", // tailwind gray-500, the old hardcoded footer text
  inputBackground: "#ffffff",
  inputTextColor: "#111827",
  inputPlaceholderColor: "#9ca3af",
  inputBorderColor: "#e5e7eb", // the old border-input default
  inputFocusBorderColor: "#14b8a6", // teal-500 focus ring, previously hardcoded
  inputRadius: "8px", // rounded-lg
  buttonBackground: "#007bff",
  buttonTextColor: "#ffffff",
  buttonRadius: "8px",
  accentColor: "#16a34a", // green-600 WhatsApp icon, previously hardcoded
  closeColor: "#4b5563", // gray-600
  closeBackground: "rgba(255,255,255,0.8)",
  borderRadius: "16px", // old fallback when dimensions.border_radius missing
} as const;

// ---------- base document builders ----------

const baseTheme = (): DesignTheme => ({
  fontFamily: LEGACY.fontFamily,
  frameless: false,
  background: {
    type: "solid",
    color: LEGACY.panelBackground,
  },
  borderRadius: LEGACY.borderRadius,
  shadow: "lg",
  padding: "comfortable",
  overlay: {
    color: LEGACY.overlayColor,
    blur: 0,
  },
  animation: {
    entry: "fade",
    durationMs: 200,
  },
});

const baseElements = (): DesignElements => ({
  // size "md" on every element maps to the legacy clamp() in the engine's
  // size tables — defaults MUST stay "md" for legacy fidelity; align "center"
  // matches the legacy text-center classes.
  heading: { color: LEGACY.headingColor, size: "md", weight: 700, align: "center", italic: false },
  subheading: { color: LEGACY.subheadingColor, size: "md", weight: 400, align: "center", italic: false },
  body: { color: LEGACY.bodyColor, size: "md", weight: 400, align: "center", italic: false },
  footer: { color: LEGACY.footerColor, size: "md", weight: 400, align: "center", italic: false },
  inputs: {
    background: LEGACY.inputBackground,
    textColor: LEGACY.inputTextColor,
    placeholderColor: LEGACY.inputPlaceholderColor,
    borderColor: LEGACY.inputBorderColor,
    focusBorderColor: LEGACY.inputFocusBorderColor,
    radius: LEGACY.inputRadius,
    borderWidth: 2, // legacy hardcoded border-2
    size: "md", // legacy h-11/h-12 field height
    customWidthPct: 0, // 0 = auto; set by builder drag-resize
    customMobileWidthPct: 0,
    customHeightPx: 0,
    customMobileHeightPx: 0,
    align: "center",
    mobileAlign: "", // "" = inherit align
  },
  button: {
    background: LEGACY.buttonBackground,
    textColor: LEGACY.buttonTextColor,
    radius: LEGACY.buttonRadius,
    size: "md",
    shadow: "none",
    fontSizePx: 0, // 0 = auto (the size scale's text classes)
    mobileFontSizePx: 0,
    customWidthPct: 0, // 0 = auto; set by builder drag-resize
    customMobileWidthPct: 0,
    customHeightPx: 0,
    customMobileHeightPx: 0,
    align: "center",
    mobileAlign: "", // "" = inherit align
  },
  icons: { accentColor: LEGACY.accentColor },
  closeButton: { color: LEGACY.closeColor, background: LEGACY.closeBackground, placement: "panel" },
  // "" / 0 = the code box follows the message's alignment and size.
  code: { align: "", mobileAlign: "", sizePx: 0, mobileSizePx: 0 },
});

const baseImage = (): DesignImage => ({
  enabled: false,
  url: "",
  mobileUrl: "",
  hideOnMobile: false,
  alt: "",
  position: "right",
  fit: "cover",
  widthPct: 50,
  customWidthPct: 0, // 0 = auto (legacy sizing); set by builder drag-resize
  customMobileWidthPct: 0,
  style: "flush", // legacy edge-to-edge
  inset: "20px",
  radius: "16px",
});

// Per-step ONLY (steps.<step>.textImage) — no design-root field to inherit
// from, so a welcome artwork can never leak onto the success step.
const baseTextImage = (): DesignTextImage => ({
  url: "",
  mobileUrl: "",
  widthPct: 0, // 0 = auto (full width); set by builder drag-resize
  mobileWidthPct: 0,
  align: "center",
  mobileAlign: "", // "" = inherit align
});

export const baseDesign = (): PopupDesign => ({
  format: "modal",
  mobileFormat: "inherit",
  contentPanel: { enabled: false, background: "#ffffff", radius: "24px", padding: "32px", maxWidth: "560px", align: "center", inset: "6%", phones: "plain" },
  position: "center",
  size: { preset: "custom" },
  theme: baseTheme(),
  elements: baseElements(),
  image: baseImage(),
  teaser: {
    enabled: false,
    mode: "off",
    text: "",
    position: "right",
    orientation: "vertical",
    background: "#111827",
    textColor: "#ffffff",
    radius: "12px",
    showIcon: true,
    action: "popup",
    actionNumber: "",
    actionMessage: "",
    actionUrl: "",
    postSignup: "hide",
    postSignupText: "",
  },
  dismiss: {
    enabled: false,
    text: "No thanks",
    style: "link",
    color: "", // "" = auto per style
    background: "", // button mode; "" = light gray
    radius: "", // button mode; "" = inherit the main button's radius
    underline: true,
    fontSizePx: 0, // 0 = default per style
  },
  hideCloseButton: false, // guarded: only honoured with another close path
  overlayClose: false, // legacy renderer never closed on overlay click
  steps: {},
});

// ---------- input ----------

/** What normalizeDesign accepts: a popup row's style_settings. Since the
 *  8 Oct 2026 migration every row holds exactly one key, `design` (the
 *  pre-2026 `welcome` / `success` colour blocks were folded into it and the
 *  server refuses them). The index signature keeps unknown keys: the flowIQ
 *  editor's draft state is this exact type. */
export type NormalizeInput = {
  design?: PopupDesignInput;
  [key: string]: unknown;
};
/** The flowIQ editor's name for the same thing (its draft state type). */
export type EditorStyleSettings = NormalizeInput;

// ---------- generic deep merge (arrays + scalars replace, objects merge) ----------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function deepMerge<T>(base: T, override: DeepPartial<T> | undefined): T {
  if (override === undefined || override === null) return base;
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override as T;
  }
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const ov = (override as Record<string, unknown>)[key];
    if (ov === undefined) continue;
    const bv = (base as Record<string, unknown>)[key];
    out[key] = isPlainObject(bv) && isPlainObject(ov) ? deepMerge(bv, ov) : ov;
  }
  return out as T;
}

// ---------- the normalizer ----------

/**
 * Maps a row's style_settings into the fully-resolved design model the
 * renderer consumes.
 *
 * Precedence, lowest → highest:
 *   base defaults (what the pre-2026 renderer painted)
 *   → style_settings.design (the stored document)
 *   → design.steps.<step> overrides
 */
export function normalizeDesign(
  styleSettings: NormalizeInput | null | undefined
): NormalizedPopupDesign {
  const design = deepMerge(baseDesign(), styleSettings?.design);

  // 4) resolve per-step designs.
  // ""-valued fontFamily/letterSpacing in STEP overrides mean "no override"
  // (the editor's inherit sentinel) — they must not clobber the base element's
  // value in the merge. At base level "" is already equivalent to unset.
  const INHERIT_SENTINEL_KEYS = new Set(["fontFamily", "letterSpacing"]);
  const pruneInheritSentinels = <T,>(obj: T): T => {
    if (!isPlainObject(obj)) return obj;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (INHERIT_SENTINEL_KEYS.has(k) && v === "") continue;
      out[k] = isPlainObject(v) ? pruneInheritSentinels(v) : v;
    }
    return out as T;
  };

  const resolveStep = (step: "welcome" | "success"): ResolvedStepDesign => {
    const overrides = design.steps?.[step];
    const theme = deepMerge(design.theme, overrides?.theme);
    const elements = deepMerge(
      design.elements,
      overrides?.elements ? pruneInheritSentinels(overrides.elements) : undefined
    );
    const image = deepMerge(design.image, overrides?.image as DeepPartial<DesignImage>);
    const textImage = deepMerge(
      baseTextImage(),
      overrides?.textImage as DeepPartial<DesignTextImage>
    );
    const size = overrides?.size
      ? deepMerge(design.size, overrides.size)
      : design.size;
    return {
      format: design.format,
      position: design.position,
      size,
      theme,
      elements,
      image,
      textImage,
    };
  };

  const welcome = resolveStep("welcome");

  // The deck wears the WELCOME skin, then steps.deck tweaks it (e.g. the
  // SVYP floating presentation: frameless + a screen-placed close).
  const overrideStep = (
    overrides: StepDesignOverrides | undefined
  ): ResolvedStepDesign =>
    overrides
      ? {
          ...welcome,
          size: overrides.size
            ? deepMerge(welcome.size, overrides.size)
            : welcome.size,
          theme: deepMerge(welcome.theme, overrides.theme),
          elements: deepMerge(
            welcome.elements,
            overrides.elements ? pruneInheritSentinels(overrides.elements) : undefined
          ),
          image: deepMerge(welcome.image, overrides.image as DeepPartial<DesignImage>),
          textImage: deepMerge(
            welcome.textImage,
            overrides.textImage as DeepPartial<DesignTextImage>
          ),
        }
      : welcome;

  return {
    design,
    welcome,
    success: resolveStep("success"),
    // The deck and scratch steps wear the WELCOME skin, then their own
    // steps.* tweaks (e.g. the SVYP floating presentation: frameless + a
    // screen-placed close).
    deck: overrideStep(design.steps?.deck),
    scratch: overrideStep(design.steps?.scratch),
    wheel: overrideStep(design.steps?.wheel),
    question: overrideStep(design.steps?.question),
    phone: overrideStep(design.steps?.phone),
  };
}
